/**
 * CaraLoom v2 — Information Architecture configuration
 * =====================================================
 *
 * Single source of truth for the post-acute-care OS reorganization.
 * Used by navigation, feature flags, role-based routing, and the v2 archive
 * plan. Edit here, not in individual components.
 *
 * Generated alongside /app/CARALOOM_2.0_BLUEPRINT.pdf.
 * Companion: /app/memory/CARALOOM_V2_ARCHIVE_PLAN.md
 */

// ── Six product suites ───────────────────────────────────────────────
// Each suite is its own monetizable surface with its own KPI + buyer.
export const PRODUCT_SUITES = [
  {
    id: 'referrals',
    name: 'CaraLoom Referrals',
    tagline: 'The fastest referral and discharge-coordination platform in post-acute care.',
    primary_buyer: 'Hospital case manager / Discharge planner',
    secondary_buyer: 'SNF/ALF placement coordinator',
    kpi: 'Time from referral submitted to provider acceptance (target: <60 min)',
    monetization: 'Per-referral take rate (facility-side) + provider SaaS seat',
    color: '#0E3D3A',
    icon: 'arrow-right-circle',
    route_root: '/referrals',
  },
  {
    id: 'workforce',
    name: 'CaraLoom Workforce',
    tagline: 'The leading marketplace for independent post-acute clinicians.',
    primary_buyer: 'Provider Director of Clinical Services',
    secondary_buyer: 'Independent clinician',
    kpi: 'Visit fill rate within 24h of need (target: >85%)',
    monetization: 'Provider SaaS + take rate on filled clinician hours',
    color: '#14B8A6',
    icon: 'users',
    route_root: '/workforce',
  },
  {
    id: 'operations',
    name: 'CaraLoom Operations',
    tagline: 'The operational command center for post-acute organizations.',
    primary_buyer: 'Provider COO / Operations Director',
    secondary_buyer: 'Executive (CEO / CFO)',
    kpi: 'Visits per clinician per day (target: +18% vs. baseline)',
    monetization: 'Per-bed/per-census SaaS',
    color: '#0F766E',
    icon: 'layout-grid',
    route_root: '/operations',
  },
  {
    id: 'compliance',
    name: 'CaraLoom Compliance',
    tagline: 'Survey-ready, audit-ready, continuously verified.',
    primary_buyer: 'Provider Compliance Officer / QAPI lead',
    secondary_buyer: 'Surveyor / Auditor',
    kpi: 'Days to survey readiness (target: zero — always ready)',
    monetization: 'Compliance-tier SaaS uplift',
    color: '#475569',
    icon: 'shield-check',
    route_root: '/compliance',
  },
  {
    id: 'revenue-cycle',
    name: 'CaraLoom Revenue Cycle',
    tagline: 'The AI-assisted financial operating system for hospice, home health, and palliative.',
    primary_buyer: 'Provider CFO / RCM Director',
    secondary_buyer: 'Billing / coding staff',
    kpi: 'Days Sales Outstanding (target: <35 days)',
    monetization: '% of net collections (typically 2.5-4%) OR per-claim fee',
    color: '#B45309',
    icon: 'banknote',
    route_root: '/revenue-cycle',
  },
  {
    id: 'care',
    name: 'CaraLoom Care',
    tagline: 'The digital care hub for patients and families.',
    primary_buyer: 'Family decision-maker (adult child)',
    secondary_buyer: 'Patient',
    kpi: 'Active families per month after primary-care episode ends',
    monetization: 'Family subscription + private-pay visit take rate',
    color: '#0EA5E9',
    icon: 'heart-pulse',
    route_root: '/care',
  },
];

// ── Four primary roles ───────────────────────────────────────────────
// Plus admin/ops as a back-of-house role.
export const ROLES = {
  facility: {
    label: 'Healthcare Facility',
    description: 'Hospital, SNF, ALF, LTC, memory care, rehab, CCRC, ILF, physician practice.',
    primary_suite: 'referrals',
    secondary_suites: ['workforce'],  // staffing requests
  },
  provider: {
    label: 'Care Provider',
    description: 'Hospice, home health, palliative care agency. Primary enterprise customer.',
    primary_suite: 'operations',
    secondary_suites: ['referrals', 'workforce', 'compliance', 'revenue-cycle'],
  },
  clinician: {
    label: 'Independent Clinician',
    description: 'RN, LPN, NP, MD, CNA, aide, social worker, chaplain, PT/OT/ST/RT, bereavement counselor.',
    primary_suite: 'workforce',
    secondary_suites: [],
  },
  family: {
    label: 'Patient & Family',
    description: 'Patient + adult children + caregiver circle.',
    primary_suite: 'care',
    secondary_suites: [],
  },
  admin: {
    label: 'CaraLoom Internal',
    description: 'Platform operations, trust & safety, compliance ops, revenue ops.',
    primary_suite: null,
    secondary_suites: ['referrals', 'workforce', 'operations', 'compliance', 'revenue-cycle', 'care'],
  },
};

// ── V1 feature taxonomy ──────────────────────────────────────────────
// V1 = ship within 6 months. The minimum platform that proves the wedge.
// V2 = ship 6-18 months. Expands the wedge into adjacent suites.
// V3 = ship 18-36 months. Platform-completeness at $10M+ ARR.
// ARCHIVED = preserved code, hidden from UI. Resurrect when geographic /
//   audience expansion makes them relevant again. Nothing is deleted.
export const FEATURE_LIFECYCLE = {
  // ── V1 ─────────────────────────────────────────────────────────
  v1: [
    // Referrals (the wedge)
    { id: 'referrals.discharge-intake',      suite: 'referrals',     legacy_id: 'F63' },
    { id: 'referrals.provider-acceptance',   suite: 'referrals',     legacy_id: 'F64' },
    { id: 'referrals.status-tracking',       suite: 'referrals',     legacy_id: 'F62' },
    { id: 'referrals.secure-messaging',      suite: 'referrals',     legacy_id: 'F27' },
    { id: 'referrals.ai-prioritization',     suite: 'referrals',     legacy_id: 'F65' },
    { id: 'referrals.partner-api',           suite: 'referrals',     legacy_id: 'F68' },

    // Workforce (supply liquidity)
    { id: 'workforce.clinician-onboarding',  suite: 'workforce',     legacy_id: 'F20' },
    { id: 'workforce.credential-verify',     suite: 'workforce',     legacy_id: 'F25' },
    { id: 'workforce.background-check',      suite: 'workforce',     legacy_id: 'F26' },
    { id: 'workforce.scheduling',            suite: 'workforce',     legacy_id: 'F40' },
    { id: 'workforce.visit-fulfillment',     suite: 'workforce',     legacy_id: 'F41' },
    { id: 'workforce.provider-roster',       suite: 'workforce',     legacy_id: null  },

    // Compliance (the regulatory moat)
    { id: 'compliance.license-monitoring',   suite: 'compliance',    legacy_id: 'F33' },
    { id: 'compliance.audit-trail',          suite: 'compliance',    legacy_id: 'F03' },
    { id: 'compliance.documentation',        suite: 'compliance',    legacy_id: 'F44' },

    { id: 'care.family-dashboard',           suite: 'care',          legacy_id: 'F16' },
    { id: 'care.secure-messaging',           suite: 'care',          legacy_id: 'F27' },
    { id: 'care.visit-tracking',             suite: 'care',          legacy_id: 'F42' },
    // Family-side "Find Clinicians" wizard + results — resurrected from
    // archived (Feb 2026 → Jun 2026 → Feb 2026) because the existing
    // surfaces in PatientDashboard, patient nav, and the Landing/Pricing
    // FinalCTAs all advertise it. Keeping the underlying flow gated led
    // to a "coming soon" dead-link UX. Public/anonymous browsing remains
    // archived via `care.public-marketplace`; this entry only unlocks
    // authenticated patient-context find-clinicians screens.
    { id: 'care.find-clinicians-page',       suite: 'care',          legacy_id: 'F61' },

    // CaraLoom 2.0 — CEO Decisions #7 / #12 / #21 (graduated from v2/v3 to v1)
    { id: 'operations.executive-dashboard',  suite: 'operations',    legacy_id: 'F73' },
    { id: 'referrals.fhir-ingest',           suite: 'referrals',     legacy_id: 'F100'},
    { id: 'rcm.denial-prediction',           suite: 'revenue-cycle', legacy_id: null  },

    // Passive clinician-supply marketing (iter128 · Feb 2026):
    // These pages were originally archived under the reasoning "no direct-
    // recruit channel yet." That's a category error — passive marketing
    // pages don't create a channel, they exist to reinforce positioning
    // ("independent clinicians who power [providers]" is literally in the
    // V1 mission). They were surfacing the ArchivedFeaturePage which read
    // as "the company is paused" to visitors. Un-gated back to V1. The
    // underlying pages (ForCliniciansPage 368 lines, DisciplineLandingPage
    // 282 lines) were never removed and remain the source of truth.
    { id: 'workforce.direct-recruit-marketing', suite: 'workforce',  legacy_id: 'F22' },
    { id: 'workforce.discipline-landings',   suite: 'workforce',     legacy_id: 'F22' },

    // Autonomous Discharge Orchestrator (v1) — feature-flagged agent that
    // runs the full referral → placement → first-visit → 30-day-watch loop
    // by composing existing engines. Honesty contract: any tool call
    // against a non-live integration is tagged mocked in the trace.
    { id: 'referrals.orchestrator',          suite: 'referrals',     legacy_id: null  },

    // Phase 7 — North Star loop additions (Jun 2026)
    { id: 'referrals.orders-signatures',     suite: 'referrals',     legacy_id: null  },
    { id: 'referrals.coverage-gate',         suite: 'referrals',     legacy_id: null  },
    { id: 'care.advance-care-planning',      suite: 'care',          legacy_id: null  },

    // Foundation
    { id: 'foundation.auth',                 suite: null,            legacy_id: 'F00' },
    { id: 'foundation.admin',                suite: null,            legacy_id: 'F90' },
    { id: 'foundation.observability',        suite: null,            legacy_id: 'F93' },
  ],

  // ── V2 (6-18 months) ───────────────────────────────────────────
  v2: [
    // Operations (the data moat surface)
    { id: 'operations.census',               suite: 'operations',    legacy_id: null  },
    { id: 'operations.productivity',         suite: 'operations',    legacy_id: null  },
    { id: 'operations.executive-dashboards', suite: 'operations',    legacy_id: 'F73' },
    { id: 'operations.route-optimization',   suite: 'operations',    legacy_id: null  },

    // Revenue Cycle (the retention engine)
    { id: 'rcm.eligibility-verification',    suite: 'revenue-cycle', legacy_id: 'F34' },
    { id: 'rcm.claim-scrub',                 suite: 'revenue-cycle', legacy_id: 'F52' },
    { id: 'rcm.claim-submit',                suite: 'revenue-cycle', legacy_id: 'F55' },
    { id: 'rcm.ai-coding',                   suite: 'revenue-cycle', legacy_id: 'F53' },
    { id: 'rcm.missed-revenue',              suite: 'revenue-cycle', legacy_id: 'F54' },
    { id: 'rcm.denial-management',           suite: 'revenue-cycle', legacy_id: null  },
    { id: 'rcm.payment-posting',             suite: 'revenue-cycle', legacy_id: null  },
    { id: 'rcm.ar-aging',                    suite: 'revenue-cycle', legacy_id: 'F91' },

    // Workforce expansion
    { id: 'workforce.contract-management',   suite: 'workforce',     legacy_id: null  },
    { id: 'workforce.banking',               suite: 'workforce',     legacy_id: 'F56' },
    { id: 'workforce.tax',                   suite: 'workforce',     legacy_id: 'F57' },
    { id: 'workforce.benefits',              suite: 'workforce',     legacy_id: 'F58' },
    { id: 'workforce.payouts',               suite: 'workforce',     legacy_id: 'F59' },

    // Telehealth + clinical notes
    { id: 'workforce.telehealth',            suite: 'workforce',     legacy_id: 'F43' },
    { id: 'workforce.clinical-notes-ai',     suite: 'workforce',     legacy_id: 'F44' },
    { id: 'workforce.voice-notes',           suite: 'workforce',     legacy_id: 'F45' },

    // Compliance expansion
    { id: 'compliance.survey-readiness',     suite: 'compliance',    legacy_id: null  },
    { id: 'compliance.quality-reporting',    suite: 'compliance',    legacy_id: null  },
    { id: 'compliance.ai-monitoring',        suite: 'compliance',    legacy_id: 'F31' },

    // AI + safety
    { id: 'ai.safety-posture',               suite: null,            legacy_id: 'F31' },
    { id: 'ai.validation-harness',           suite: null,            legacy_id: 'F32' },
    { id: 'ai.audit-envelope',               suite: null,            legacy_id: 'F72' },

    // Readmission engine (ties Referrals → Workforce → Operations)
    { id: 'operations.readmission-engine',   suite: 'operations',    legacy_id: 'F66' },
    { id: 'operations.first-vitals-nudge',   suite: 'operations',    legacy_id: 'F67' },
  ],

  // ── V3 (18-36 months) ──────────────────────────────────────────
  v3: [
    { id: 'rcm.revenue-forecasting',         suite: 'revenue-cycle', legacy_id: null  },
    { id: 'rcm.denial-prediction-ai',        suite: 'revenue-cycle', legacy_id: null  },
    { id: 'operations.staffing-prediction',  suite: 'operations',    legacy_id: null  },
    { id: 'workforce.marketplace-discovery', suite: 'workforce',     legacy_id: 'F61' },
    { id: 'care.private-pay-marketplace',    suite: 'care',          legacy_id: 'F12' },
    { id: 'care.ai-care-assistant',          suite: 'care',          legacy_id: 'F11' },
    { id: 'compliance.regulatory-workflows', suite: 'compliance',    legacy_id: null  },

    // FHIR + integration mesh
    { id: 'platform.fhir-encounter-mirror',  suite: null,            legacy_id: 'F100'},
    { id: 'platform.ehr-integrations',       suite: null,            legacy_id: null  },

    // Payer-facing (only at $10M+ ARR)
    { id: 'platform.payer-vbc-dashboard',    suite: null,            legacy_id: 'F70' },
    { id: 'platform.payer-cohort-report',    suite: null,            legacy_id: 'F71' },
  ],

  // ── Archived (preserved, hidden) ───────────────────────────────
  // These features ship to code but are flag-gated OFF in V1/V2/V3.
  // Resurrection criteria documented in /app/memory/CARALOOM_V2_ARCHIVE_PLAN.md
  archived: [
    // Hyper-local SEO surfaces — bring back when there is a 100+ clinician
    // beachhead in any city. National landing pages create false expectations.
    { id: 'seo.local-care-pages',            suite: null,            legacy_id: 'F14',
      resurrect_when: 'A city has 80+ verified active clinicians and signed at least 3 facility referral partners.' },
    { id: 'seo.service-landing-pages',       suite: null,            legacy_id: 'F13',
      resurrect_when: 'Provider-side network covers a ZCTA-level density target.' },
    { id: 'seo.content-velocity-engine',     suite: null,            legacy_id: 'F80',
      resurrect_when: 'Family-side becomes an explicit growth channel (Phase 3+ of company).' },
    { id: 'seo.care-library',                suite: null,            legacy_id: 'F19',
      resurrect_when: 'Care-suite reaches PMF (>10K active family accounts).' },
    { id: 'seo.authority-articles',          suite: null,            legacy_id: 'F81',
      resurrect_when: 'Care-suite reaches PMF.' },

    // Discipline landing pages — iter128 (Feb 2026): moved back to V1.
    // Passive marketing surfaces don't require a direct-recruit channel;
    // they reinforce the "independent clinicians who power providers"
    // half of V1 positioning. Kept the legacy entry stub here as a
    // historical marker in case someone greps for the old archive
    // reasoning — do NOT re-add without confirming with the platform
    // decision log.
    { id: 'workforce.discipline-advisors',   suite: null,            legacy_id: 'F29',
      resurrect_when: 'Workforce direct-recruit channel is live.' },
    // (workforce.direct-recruit-marketing + workforce.discipline-landings
    //  moved to v1 — see FEATURE_LIFECYCLE.v1 above.)

    // Note: care.public-marketplace + care.find-clinicians-page used to be
    // resurrected to V1 (Feb 2026). The public marketplace stays archived
    // (no anonymous family-pay browsing yet), but care.find-clinicians-page
    // is back in v1 because authenticated family surfaces (PatientDashboard
    // hero, patient nav, FinalCTA "Find care for my family") all link to
    // it — keeping it archived produced "coming soon" dead-links.
    { id: 'care.public-marketplace',         suite: null,            legacy_id: 'F12',
      resurrect_when: 'Family-side is an explicit funded growth channel and a measurable acquisition source.' },
    { id: 'consumer.family-status-view',     suite: null,            legacy_id: 'F16',
      resurrect_when: 'Family-side has signed BAAs and a payment rail (Phase 3+).' },
    { id: 'consumer.public-clinician-browse', suite: null,           legacy_id: 'F61',
      resurrect_when: 'Family-side discovery has measurable demand & legal sign-off on directory listing.' },

    { id: 'care.saved-clinicians',           suite: null,            legacy_id: 'F17',
      resurrect_when: 'Care-suite has retention signal.' },
    { id: 'workforce.practice-migration',    suite: null,            legacy_id: 'F28',
      resurrect_when: 'Provider customer base exceeds 50 paying organizations.' },

    // Micro-agency — confusing dual-positioning in V1
    { id: 'workforce.micro-agency-mode',     suite: null,            legacy_id: 'F103',
      resurrect_when: 'Provider-tier customer base saturates.' },

    // i18n — only useful after English PMF is locked
    { id: 'platform.i18n',                   suite: null,            legacy_id: 'F101',
      resurrect_when: 'Single-locale ARR exceeds $20M and there is signed multilingual demand.' },

    // Practice coach — duplicates Operations dashboards in V2
    { id: 'workforce.ai-practice-coach',     suite: null,            legacy_id: 'F105',
      resurrect_when: 'Independent-clinician direct-recruit motion is funded.' },

    // Care library + AI navigator (consumer)
    { id: 'care.ai-care-navigator',          suite: null,            legacy_id: 'F11',
      resurrect_when: 'Care-suite enters V3.' },

    // Saved searches / Lynk / care-circle threads
    { id: 'care.saved-searches',             suite: null,            legacy_id: 'F17',
      resurrect_when: 'Care-suite enters V3.' },
    { id: 'care.care-requests-lynk',         suite: null,            legacy_id: 'F104',
      resurrect_when: 'Care-suite enters V3.' },

    // Settlement engine + escrow ledger — not needed until $100K+ MRR
    { id: 'platform.settlement-engine',      suite: null,            legacy_id: 'F102',
      resurrect_when: 'GMV through CaraLoom exceeds $1M/month.' },

    // Monetization surfaces — retired while in hospital pilot (Fix-Now Part 2,
    // Jul 2026). /pricing now renders the honest pilot page; the calculator
    // route stays behind this gate. FeeSchedules.js + PricingCalculator.jsx
    // are preserved untouched for when monetization is re-enabled.
    { id: 'monetization.pricing-calculator', suite: null,            legacy_id: null,
      resurrect_when: 'Public monetization surfaces re-enabled after pilot pricing is locked.' },
  ],
};

// ── Navigation by role ───────────────────────────────────────────────
// Each role sees only the suites that matter to them. Other suites are
// archived from their navigation but still accessible to admin/internal.
export const NAVIGATION_BY_ROLE = {
  facility: [
    { suite: 'referrals',  label: 'Refer a Patient',     primary: true  },
    { suite: 'workforce',  label: 'Request Staffing',    primary: false },
  ],
  provider: [
    { suite: 'operations',     label: 'Command Center',      primary: true  },
    { suite: 'referrals',      label: 'Incoming Referrals',  primary: false },
    { suite: 'workforce',      label: 'Workforce',           primary: false },
    { suite: 'compliance',     label: 'Compliance',          primary: false },
    { suite: 'revenue-cycle',  label: 'Revenue Cycle',       primary: false },
  ],
  clinician: [
    { suite: 'workforce',  label: 'My Workforce',        primary: true  },
  ],
  family: [
    { suite: 'care',       label: 'My Care',             primary: true  },
  ],
  admin: [
    { suite: 'operations',     label: 'Platform Operations',     primary: true  },
    { suite: 'referrals',      label: 'Referrals Ops',           primary: false },
    { suite: 'workforce',      label: 'Workforce Ops',           primary: false },
    { suite: 'compliance',     label: 'Trust & Safety',          primary: false },
    { suite: 'revenue-cycle',  label: 'Revenue Ops',             primary: false },
    { suite: 'care',           label: 'Care Ops',                primary: false },
  ],
};

// ── Feature-flag helper ──────────────────────────────────────────────
const ALL_V1_IDS  = new Set(FEATURE_LIFECYCLE.v1.map((f) => f.id));
const ALL_V2_IDS  = new Set(FEATURE_LIFECYCLE.v2.map((f) => f.id));
const ALL_V3_IDS  = new Set(FEATURE_LIFECYCLE.v3.map((f) => f.id));
const ARCHIVED_IDS = new Set(FEATURE_LIFECYCLE.archived.map((f) => f.id));

/**
 * Returns the version tier of a feature ID. Used by route guards and
 * navigation builders to decide whether to render a link or 404.
 */
export const featureTier = (featureId) => {
  if (ALL_V1_IDS.has(featureId))  return 'v1';
  if (ALL_V2_IDS.has(featureId))  return 'v2';
  if (ALL_V3_IDS.has(featureId))  return 'v3';
  if (ARCHIVED_IDS.has(featureId)) return 'archived';
  return 'unknown';
};

/**
 * Current shipping tier — read from env. Defaults to 'v1' so the platform
 * boots in the minimum-viable configuration. Set REACT_APP_CARALOOM_TIER
 * to 'v2' / 'v3' to expose later-stage features. Archived features are
 * never exposed via this flag — they only resurface via explicit re-add
 * to FEATURE_LIFECYCLE.v1/v2/v3.
 */
export const currentTier = () =>
  process.env.REACT_APP_CARALOOM_TIER || 'v1';

export const isFeatureLive = (featureId) => {
  const tier = featureTier(featureId);
  if (tier === 'archived' || tier === 'unknown') return false;
  const order = { v1: 1, v2: 2, v3: 3 };
  return order[tier] <= order[currentTier()];
};

// ── LIVE_SURFACES — the strict frontend allowlist (Phase 7) ─────────────
// Route prefixes that are LIVE for users. Anything outside this list is
// archived (code preserved, surface hidden). SEO/public pages stay fully
// live, unauthenticated, and indexable. Backed by the pytest lint in
// backend/tests/test_phase7_north_star.py — editing this list is a
// product decision, not a refactor.
export const LIVE_SURFACES = [
  // Public / SEO (unauthenticated, indexable)
  // Fix-Now Part 3 (Feb 2026 iter): /services (hub + service landings) moved
  // out of the live surface list — the copy inside services.js still
  // advertises the retired consumer marketplace. Routes are preserved in
  // App.js but gated behind the archived `seo.service-landing-pages` flag,
  // so they render <ArchivedFeaturePage />. Restore by re-adding '/services'
  // here AND flipping the flag out of FEATURE_LIFECYCLE.archived.
  '/', '/about', '/how-it-works', '/for-discharge-planners',
  '/for-clinicians', '/for-families', '/pricing', '/contact', '/trust-safety',
  '/help', '/privacy', '/terms', '/login', '/register', '/auth',  // North-star loop (authenticated)
  '/planner', '/admin', '/clinician', '/agency', '/patient', '/family',
  '/spine', '/referrals', '/provider', '/payer', '/compliance',
  // Advance Care Planning (gated by the advance_care_planning entitlement)
  '/acp',
];

// Archived surface prefixes — flag-gated OFF, render ArchivedFeaturePage.
export const ARCHIVED_SURFACES = [
  '/marketplace', '/resources', '/home-care', '/rcm', '/telehealth',
  '/revenue-cycle',
];

export const isSurfaceLive = (pathname) => {
  if (!pathname) return false;
  const hit = (p) => pathname === p || (p !== '/' && pathname.startsWith(p + '/'));
  if (ARCHIVED_SURFACES.some(hit)) return false;
  return LIVE_SURFACES.some(hit) || pathname === '/';
};

// ── Mission statement (single source of truth) ──────────────────────
export const CARALOOM_MISSION =
  "Every patient leaving a healthcare facility should seamlessly receive the " +
  "right care, from the right provider, supported by the right clinician — " +
  "without delays, fragmentation, or unnecessary administrative burden.";

export const CARALOOM_POSITIONING =
  "CaraLoom is the AI Operating System for the Post-Acute Care Ecosystem.";

export const CARALOOM_NOT =
  "CaraLoom is not an EMR. CaraLoom integrates with EMRs while becoming the " +
  "operational layer for referrals, workforce, compliance, AI automation, " +
  "revenue cycle, scheduling, care coordination, and family engagement.";
